import {
  Body,
  Box,
  Checkbox,
  Drawer,
  Flex,
  Heading,
  useToast,
} from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { generatePath, useHistory } from 'react-router';

import { Button, LinkExternal } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { External, Route } from 'src/shared/route';
import { EventType, emitPageAction } from 'src/shared/telemetry';

import {
  useActiveOriginalImage,
  useHomeownerAppContext,
  useViewportSize,
} from '../../hooks';
import { useUploadOriginalImage } from '../../hooks/useUploadOriginalImage';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { OriginalImageStatus } from '../../static_values';
import { isSample } from '../../types';

import { SpaceTypeSelector } from './SpaceTypeSelector';
import { UploadImageBox } from './UploadImageBox';

const VERTICAL_DRAWER_PADDING_PIXELS = 40;
const UPLOAD_IMAGE_STATUS_POLLING_INTERVAL = 1000;

const UploadImage = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const history = useHistory();
  const errorToast = useToast({
    duration: 5000,
    isClosable: true,
  });

  const { height: viewportHeight } = useViewportSize();
  const [uploadedImageId, setUploadedImageId] = useState<number | undefined>();
  const [spaceType, setSpaceType] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const {
    imageFileToUpload,
    setImageFileToUpload,
    user,
    trackingData,
    loggedIn,
    setCurrentMenu,
  } = useHomeownerAppContext();

  const { setHomeownerAppSpaceType } = useGlobalStorageContext();
  const { image, startPolling, stopPolling } = useActiveOriginalImage({
    originalImageId: uploadedImageId,
    options: { fetchPolicy: 'network-only' },
  });

  useEffect(() => {
    if (uploadedImageId && !image?.status) {
      startPolling(UPLOAD_IMAGE_STATUS_POLLING_INTERVAL);
    }

    return stopPolling;
  }, [startPolling, uploadedImageId, stopPolling, image?.status]);

  const uploadImage = useUploadOriginalImage();

  useEffect(() => {
    const url = imageFileToUpload ? URL.createObjectURL(imageFileToUpload) : '';
    setImageUrl(url);
    return url ? () => URL.revokeObjectURL(url) : () => null;
  }, [imageFileToUpload]);

  const handleClose = () => {
    setIsUploading(false);
    setImageFileToUpload();
    setSpaceType('');
    setAgreedToTerms(false);
    setUploadedImageId(undefined);
  };

  useEffect(() => {
    // AiOriginalImage status check
    if (!uploadedImageId || !isUploading) return;
    if (isSample(image)) return;
    if (!image) return;

    if (image?.status !== null) {
      stopPolling();
      setSpaceType('');
      setAgreedToTerms(false);
      setImageFileToUpload();
      setIsUploading(false);
      setUploadedImageId(undefined);
    }
    if (image?.status === OriginalImageStatus.FAILED) {
      history.replace({
        pathname: Route.HOMEOWNER_APP_HOME.path,
        state: {
          uploadErrorMessage: image.statusDescription,
        },
      });
    } else if (image?.status === OriginalImageStatus.DONE) {
      // customize-prototype redirect
      if (
        history.location.pathname === Route.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.path
      ) {
        const displayImageId = image.aiDisplayImages?.[0]?.id;

        if (displayImageId) {
          history.push({
            pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
              aiDisplayImageId: displayImageId,
            }),
            state: undefined,
          });
        }
      } else {
        history.push(
          generatePath(Route.HOMEOWNER_APP_ORIGINAL.path, {
            aiOriginalImageId: image.id,
          }),
          { from: 'upload' },
        );
      }
    }
  }, [
    isUploading,
    history,
    image,
    image?.status,
    stopPolling,
    setImageFileToUpload,
    uploadedImageId,
  ]);

  if (!imageFileToUpload) return null;

  const handleSubmit = async () => {
    // check if user is logged in
    if (
      !loggedIn &&
      history.location.pathname !== Route.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.path
    ) {
      setCurrentMenu(HomeownerAppMenuNames.SIGN_IN);
      setHomeownerAppSpaceType(spaceType);
      return;
    }

    setIsUploading(true);
    try {
      const uploadedImage = await uploadImage({
        image: imageFileToUpload,
        email: user?.email,
        spaceType,
      });

      const newOriginalImageId = uploadedImage?.id;
      setUploadedImageId(newOriginalImageId);

      if (newOriginalImageId) {
        emitPageAction(EventType.DESIGNER_ORIGINAL_IMAGE_UPLOADED, {
          aiOriginalImageId: `${newOriginalImageId}`,
        });
      }
    } catch (e) {
      errorToast({
        status: 'error',
        description: t('uploadPhotoSheet.uploadErrorMessage'),
      });
      setIsUploading(false);
    }
  };

  return (
    <Drawer
      autoFocus={false}
      isOpen={!!imageUrl}
      onClose={handleClose}
      placement="bottom"
      size="large" // not working, MEOW-2997
    >
      <Box
        alignItems="flex-start"
        backgroundColor="white"
        flexDirection={{ base: 'column', tablet: 'row-reverse' }}
        height={{
          base: `calc(${viewportHeight} - ${VERTICAL_DRAWER_PADDING_PIXELS}px)`,
          tablet: 800,
        }}
        overflow="auto"
        paddingBottom={{ base: 400, tablet: 600 }}
        paddingTop={{ base: undefined, tablet: 600 }}
      >
        <UploadImageBox
          display={{ base: 'none', tablet: 'flex' }}
          imageUrl={imageUrl}
        />
        <Box
          flexDirection="column"
          height="100%"
          maxWidth={{ base: '100%', tablet: '60%' }}
          paddingRight={{ tablet: 300 }}
        >
          <Flex
            alignItems="center"
            gap={{ base: 200, tablet: 400 }}
            paddingBottom={{ base: 600, tablet: 0 }}
            paddingTop={{ base: 600, tablet: 0 }}
          >
            <Heading
              flex={5}
              fontWeight={600}
              maxWidth={{ base: 700, tablet: 750 }}
              size={{ base: 600, tablet: 800 }}
            >
              {t('uploadImage.addLabel')}
            </Heading>
            <UploadImageBox
              display={{ base: 'flex', tablet: 'none' }}
              imageUrl={imageUrl}
            />
          </Flex>
          <SpaceTypeSelector
            setSpaceType={setSpaceType}
            spaceType={spaceType}
          />
          <Box
            alignItems={{ base: 'center', tablet: 'flex-start' }}
            border={{ base: '1px solid transparent', tablet: 'unset' }}
            flexDirection="column"
            gap={200}
            height="100%"
            minWidth="100%"
            opacity={spaceType ? 1 : 0}
            transition="opacity 0.2s ease-in-out"
          >
            <Flex flex={1} flexDirection="column" justifyContent="flex-end">
              <Checkbox
                alignItems="flex-start"
                checked={agreedToTerms}
                marginLeft={100}
                onChange={e => {
                  setAgreedToTerms(e.target.checked);
                }}
              >
                <Body
                  color="textLight"
                  marginTop={0}
                  paddingTop={0}
                  size={{ base: 300, tablet: 400 }}
                >
                  <Trans
                    components={{
                      SupportEmailLink: (
                        <LinkExternal
                          color="inherit"
                          href={External.SUPPORT_EMAIL.path}
                        />
                      ),
                    }}
                    i18nKey="uploadImage.legalText"
                    t={t}
                  />
                </Body>
              </Checkbox>
            </Flex>
            <Button
              isDisabled={!spaceType || !agreedToTerms}
              isLoading={isUploading}
              onClick={handleSubmit}
              optionalTrackingProperties={{
                ...trackingData,
                button_location: 'bottom-main-page',
                track_id: 'tos-generate-images',
                primary_cta: true,
              }}
              width={{ base: '100%', tablet: 'auto' }}
            >
              {t('uploadImage.createDesigns')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export { UploadImage };
